import { Alignment, Fit, Layout, Rive, RuntimeLoader, type ViewModelInstance } from '@rive-app/canvas';

// The HUD's animated ornaments, one artboard each in assets/ui/hud.riv (source:
// rive/hud/). Text always stays in the DOM. Every host element carries a CSS
// version of its ornament, kept until Rive has painted and kept for good if the
// runtime or the file fails to load: the game never waits on decoration.
RuntimeLoader.setWasmUrl('rive.wasm');
RuntimeLoader.setWasmFallbackUrl(null);

let file: Promise<ArrayBuffer | null> | null = null;
const load = (): Promise<ArrayBuffer | null> => file ??= fetch('assets/ui/hud.riv').then(response => response.ok ? response.arrayBuffer() : null).catch(() => null);
const reduced = (): boolean => document.documentElement.dataset.motion === 'reduced';
const live = new Set<ViewModelInstance>();
// Reduced motion is a live setting: ornaments already on screen follow it at once.
new MutationObserver(() => { for (const model of live) { const still = model.boolean('reducedMotion'); if (still) still.value = reduced(); } })
  .observe(document.documentElement, { attributes: true, attributeFilter: ['data-motion'] });

export interface Ornament {
  number(name: string, value: number): void;
  boolean(name: string, value: boolean): void;
  fire(name: string): void;
  dispose(): void;
}

/** Mounts one artboard in `host`. Values set before the file loads are applied once it does. */
export function ornament(host: HTMLElement, artboard: 'Seal' | 'Sigil'): Ornament {
  const numbers = new Map<string, number>(), booleans = new Map<string, boolean>();
  let model: ViewModelInstance | null = null, rive: Rive | null = null, disposed = false;
  void load().then(buffer => {
    if (!buffer || disposed) return;
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    host.append(canvas);
    rive = new Rive({
      buffer: buffer.slice(0), canvas, artboard, stateMachines: 'Main', autoplay: true, autoBind: true,
      isTouchScrollEnabled: true, shouldDisableRiveListeners: true,
      layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
      onLoad: () => {
        rive!.resizeDrawingSurfaceToCanvas();
        model = rive!.viewModelInstance;
        for (const [name, value] of numbers) { const property = model?.number(name); if (property) property.value = value; }
        for (const [name, value] of booleans) { const property = model?.boolean(name); if (property) property.value = value; }
        const still = model?.boolean('reducedMotion'); if (still) still.value = reduced();
        if (model) live.add(model);
        host.classList.add('rive');
      },
      onLoadError: () => { canvas.remove(); rive?.cleanup(); rive = null; },
    });
  });
  return {
    number(name, value) { numbers.set(name, value); const property = model?.number(name); if (property) property.value = value; },
    boolean(name, value) { booleans.set(name, value); const property = model?.boolean(name); if (property) property.value = value; },
    fire(name) { if (!reduced()) model?.trigger(name)?.trigger(); },
    dispose() { disposed = true; if (model) live.delete(model); rive?.cleanup(); rive = null; model = null; },
  };
}
